/**
 * components/CreateIntentDrawer.tsx
 * Create Intent drawer (softknack). Verbatim from HTML (~801-876). 560px.
 * Name/Owner/Spine are the required gates; everything is local state.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, Pressable, ScrollView, TextInput } from 'react-native';
import { useTheme } from '../context/themeContext';
import Icon from '../components/icon';
import { Chip, Button, Drawer } from '../components/voiceUi';
import AppDropdown from '../components/appDropdown/appDropdown';
import { OWNERS } from '../data/voiceMock';
import { slugify } from '../utils/format';
import { createIntent, listMcpTools, type McpTool } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

// Backend ownerRef (platform product slug) for each FE owner key. 'calendar'→'cal'
// matches the platform registry; the rest are 1:1. (Interim: tool lists are static
// until the backbone MCP tool-picker proxy lands.)
const OWNER_REF: Record<string, string> = {
  calendar: 'cal', billing: 'billing', tickets: 'tickets', orders: 'orders', crm: 'crm', hospitality: 'hospitality',
};

export default function CreateIntentDrawer({ open, onClose, onCreated }:
  { open: boolean; onClose: () => void; onCreated?: () => void }) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [submitting, setSubmitting] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [ownerIdx, setOwnerIdx] = useState(-1); // -1 = "Select owner…"
  const [spineIdx, setSpineIdx] = useState(-1);
  const [desc, setDesc] = useState('');
  const [prompt, setPrompt] = useState('');
  const [enrich, setEnrich] = useState<string[]>([]);
  const [utter, setUtter] = useState<string[]>([]);
  const [utterInput, setUtterInput] = useState('');

  // Real MCP registry (name + role) — fetched once when the drawer opens.
  const [tools, setTools] = useState<McpTool[]>([]);
  const [toolsLive, setToolsLive] = useState(true);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void listMcpTools().then(({ data, live }) => { if (!cancelled) { setTools(data); setToolsLive(live); } });
    return () => { cancelled = true; };
  }, [open]);

  const owner = ownerIdx >= 0 ? OWNERS[ownerIdx] : null;
  // Spine = tools whose role is SPINE or BOTH; enrichment = ENRICHMENT or BOTH.
  const spineOpts = useMemo(() => tools.filter(t => t.role === 'SPINE' || t.role === 'BOTH').map(t => t.name), [tools]);
  const enrichOpts = useMemo(() => tools.filter(t => t.role === 'ENRICHMENT' || t.role === 'BOTH').map(t => t.name), [tools]);
  const spine = spineIdx >= 0 ? spineOpts[spineIdx] : null;
  // Real slots of the selected spine tool (from its registered input schema).
  const spineSlots = useMemo(() => tools.find(t => t.name === spine)?.slots ?? [], [tools, spine]);

  const hasName = name.trim().length > 0;
  const hasOwner = !!owner;
  const hasSpine = !!spine;
  const allSet = hasName && hasOwner && hasSpine;
  const slug = hasName ? slugify(name) : '—';

  const missing: string[] = [];
  if (!hasName) missing.push('name');
  if (!hasOwner) missing.push('owner');
  if (!hasSpine) missing.push('spine tool');
  const missTxt = missing.length ? `Still needed: ${missing.join(', ')}` : 'All required fields set';

  // Cycle owner select through options + initial placeholder.

  // Create on the backend: identity is name+ownerRef+spineTool (immutable after).
  // Enrichment/version/utterances are follow-on authoring steps (added next).
  const submit = async () => {
    if (!owner || !spine || submitting) return;
    setSubmitting(true);
    try {
      // Snapshot the spine tool's real slots into the version template, so the
      // authored v1 records what the AI collects (name → {required, description}).
      const template = spineSlots.length
        ? Object.fromEntries(spineSlots.map(s => [s.name, { required: s.required, description: s.description }]))
        : undefined;
      const resp = await createIntent({
        name: name.trim(),
        ownerKind: owner.kind as 'PRODUCT' | 'VERTICAL',
        ownerRef: OWNER_REF[owner.key] ?? owner.key,
        spineTool: spine,
        category: category.trim() || undefined,
        description: desc.trim() || undefined,
        intentPrompt: prompt.trim() || undefined,
        template,
        enrichment: enrich.length ? enrich : undefined,
      });
      showSuccess(resp, 'Intent created');
      onCreated ? onCreated() : onClose();
    } catch (err) {
      handleApiError(err, 'Could not create intent');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleEnrich = (tool: string) =>
    setEnrich(e => e.includes(tool) ? e.filter(x => x !== tool) : [...e, tool]);
  const addUtter = () => {
    const v = utterInput.trim();
    if (!v) return;
    setUtter(u => [...u, v]);
    setUtterInput('');
  };
  const removeUtter = (idx: number) => setUtter(u => u.filter((_, i) => i !== idx));

  const labelStyle = { fontSize: 10.5, fontWeight: '600' as const, letterSpacing: 0.55, textTransform: 'uppercase' as const, color: c.ink4, fontFamily: theme.font.sans };
  const inputStyle = { height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.card, color: c.ink, fontSize: 13, fontFamily: theme.font.sans } as any;
  const textareaStyle = { minHeight: 64, paddingHorizontal: 12, paddingVertical: 9, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, backgroundColor: c.card, color: c.ink, fontSize: 13, fontFamily: theme.font.sans, textAlignVertical: 'top' as const } as any;

  const RequiredBadge = () => (
    <View style={{ backgroundColor: c.ink, borderRadius: 20, paddingHorizontal: 6, paddingVertical: 1 }}>
      <Text style={{ fontSize: 9, fontWeight: '600', letterSpacing: 0.36, color: c.paper, fontFamily: theme.font.sans }}>REQUIRED</Text>
    </View>
  );
  const OptionalTag = () => <Text style={{ fontSize: 9, fontWeight: '500', color: c.ink4, fontFamily: theme.font.sans }}>optional</Text>;

  // Check-circle marker (filled ink when satisfied, else outline ring).
  const Check = ({ ok }: { ok: boolean }) => (
    <View style={{ width: 16, height: 16, borderRadius: 999, alignItems: 'center', justifyContent: 'center', borderWidth: ok ? 0 : 1, borderColor: c.lineStrong, backgroundColor: ok ? c.ink : 'transparent' }}>
      {ok && <Icon name="check" size={10} color={c.paper} />}
    </View>
  );

  return (
    <Drawer open={open} onClose={onClose} width={560}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, paddingHorizontal: 18, paddingVertical: 16, borderBottomWidth: 1, borderBottomColor: c.line }}>
        <View style={{ flex: 1 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <Text style={{ fontWeight: '600', fontSize: 16, letterSpacing: -0.16, color: c.ink, fontFamily: theme.font.sans }}>New intent</Text>
            <Chip kind="mono">{slug}</Chip>
          </View>
          <Text style={{ fontSize: 11.5, color: c.ink3, fontFamily: theme.font.sans }}>Softknack-authored · added to the global catalog</Text>
        </View>
        <Pressable onPress={onClose} style={{ width: 30, height: 30, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: c.line, borderRadius: 7, backgroundColor: c.card }}>
          <Icon name="x" size={15} color={c.ink} />
        </Pressable>
      </View>

      {/* Body */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 18, gap: 18 }}>
        {/* Required checklist */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 14, paddingVertical: 11, borderWidth: 1, borderColor: c.line, borderRadius: 9, backgroundColor: c.paper2 }}>
          <Text style={{ fontSize: 11, fontWeight: '600', color: c.ink3, fontFamily: theme.font.sans }}>Required</Text>
          {([['Name', hasName], ['Owner', hasOwner], ['Spine tool', hasSpine]] as [string, boolean][]).map(([lbl, ok]) => (
            <View key={lbl} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Check ok={ok} />
              <Text style={{ fontSize: 12, color: c.ink2, fontFamily: theme.font.sans }}>{lbl}</Text>
            </View>
          ))}
        </View>

        {/* Name + Category */}
        <View style={{ flexDirection: 'row', gap: 14 }}>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}><Text style={labelStyle}>Intent name</Text><RequiredBadge /></View>
            <TextInput value={name} onChangeText={setName} placeholder="e.g. Get directions" placeholderTextColor={c.ink4} style={inputStyle} />
          </View>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}><Text style={labelStyle}>Category</Text><OptionalTag /></View>
            <TextInput value={category} onChangeText={setCategory} placeholder="e.g. Scheduling" placeholderTextColor={c.ink4} style={inputStyle} />
          </View>
        </View>

        {/* Owner + Spine */}
        <View style={{ flexDirection: 'row', gap: 14 }}>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}><Text style={labelStyle}>Owner</Text><RequiredBadge /></View>
            <AppDropdown
              value={owner ? owner.label : ''}
              placeholder="Select owner…"
              items={OWNERS.map((o) => ({ label: o.label, value: o.label }))}
              onSelect={(label) => setOwnerIdx(OWNERS.findIndex((o) => o.label === label))}
              compact
              maxVisible={5}
            />
          </View>
          <View style={{ flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}><Text style={labelStyle}>Spine tool</Text><RequiredBadge /></View>
            <AppDropdown
              value={spine ?? ''}
              placeholder={spineOpts.length ? 'Select spine tool…' : (toolsLive ? 'No tools available' : 'Could not load tools')}
              items={spineOpts.map((name) => ({ label: name, value: name }))}
              disabled={spineOpts.length === 0}
              onSelect={(name) => setSpineIdx(spineOpts.indexOf(name))}
              compact
              maxVisible={5}
            />
          </View>
        </View>

        {/* Description */}
        <View style={{ borderTopWidth: 1, borderTopColor: c.line, paddingTop: 16 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}><Text style={labelStyle}>Description</Text><OptionalTag /></View>
          <TextInput value={desc} onChangeText={setDesc} multiline placeholder="What does the caller want when this intent fires?" placeholderTextColor={c.ink4} style={textareaStyle} />
        </View>

        {/* Intent prompt */}
        <View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 6 }}><Text style={labelStyle}>Intent prompt · versioned</Text><OptionalTag /></View>
          <TextInput value={prompt} onChangeText={setPrompt} multiline placeholder="How should the agent handle this once detected? (becomes v1)" placeholderTextColor={c.ink4} style={[textareaStyle, { minHeight: 80, fontFamily: theme.font.mono, fontSize: 12 }]} />
        </View>

        {/* Slots */}
        <View>
          <Text style={[labelStyle, { marginBottom: 8 }]}>Slots (from spine schema)</Text>
          {!spine ? (
            <View style={{ paddingHorizontal: 12, paddingVertical: 10, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, borderStyle: 'dashed' }}>
              <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.sans }}>Pick a spine tool to auto-derive the required slots.</Text>
            </View>
          ) : spineSlots.length === 0 ? (
            <View style={{ paddingHorizontal: 12, paddingVertical: 10, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, borderStyle: 'dashed' }}>
              <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.sans }}>This tool takes no input — no slots to collect.</Text>
            </View>
          ) : (
            <View style={{ gap: 6 }}>
              {spineSlots.map(sl => (
                <View key={sl.name} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 12, paddingVertical: 9, borderWidth: 1, borderColor: c.line, borderRadius: 8, backgroundColor: c.paper2 }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink }}>{sl.name}</Text>
                  <Text style={{ fontSize: 10, fontWeight: '600', color: sl.required ? c.ink3 : c.ink4 }}>{sl.required ? 'required' : 'optional'}</Text>
                  <Text numberOfLines={1} style={{ flex: 1, textAlign: 'right', fontSize: 11, color: c.ink4, fontFamily: theme.font.sans }}>{sl.description}</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {/* Enrichment tools */}
        <View>
          <Text style={[labelStyle, { marginBottom: 8 }]}>Enrichment tools</Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 7 }}>
            {enrichOpts.map(tool => {
              const on = enrich.includes(tool);
              return (
                <Pressable key={tool} onPress={() => toggleEnrich(tool)}
                  style={{ height: 28, paddingHorizontal: 11, borderRadius: 20, borderWidth: 1, alignItems: 'center', justifyContent: 'center', borderColor: on ? c.ink : c.line, backgroundColor: on ? c.ink : c.card }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: 11.5, color: on ? c.paper : c.ink3 }}>{tool}</Text>
                </Pressable>
              );
            })}
          </View>
        </View>

        {/* Sample utterances */}
        <View>
          <Text style={[labelStyle, { marginBottom: 8 }]}>Sample utterances</Text>
          <View style={{ flexDirection: 'row', gap: 8, marginBottom: 9 }}>
            <TextInput value={utterInput} onChangeText={setUtterInput} onSubmitEditing={addUtter}
              placeholder="Type a caller phrase and press Enter" placeholderTextColor={c.ink4}
              style={[inputStyle, { flex: 1 }]} />
            <Button kind="default" label="Add" onPress={addUtter} />
          </View>
          {utter.length > 0 && (
            <View style={{ gap: 6 }}>
              {utter.map((u, idx) => (
                <View key={`${u}-${idx}`} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingHorizontal: 12, paddingVertical: 8, borderWidth: 1, borderColor: c.line, borderRadius: 8 }}>
                  <Text style={{ fontSize: 12.5, color: c.ink2, fontFamily: theme.font.sans }}>“{u}”</Text>
                  <Pressable onPress={() => removeUtter(idx)}><Icon name="x" size={14} color={c.ink4} /></Pressable>
                </View>
              ))}
            </View>
          )}
        </View>
      </ScrollView>

      {/* Footer */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingHorizontal: 18, paddingVertical: 12, borderTopWidth: 1, borderTopColor: c.line, backgroundColor: c.paper2 }}>
        <Text style={{ fontSize: 11, color: c.ink4, fontFamily: theme.font.sans }}>{missTxt}</Text>
        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Button kind="default" label="Cancel" onPress={onClose} />
          <Button kind="primary" label={submitting ? 'Creating…' : 'Create intent'} disabled={!allSet || submitting} onPress={submit} style={{ opacity: allSet && !submitting ? 1 : 0.4 }} />
        </View>
      </View>
    </Drawer>
  );
}
